import { useState, useCallback } from "react";
import { useUrlState } from "./useUrlState";
import { parseArrayParam, joinArrayParam } from "./urlParamUtils";
import {
  loadStorageKeys,
  saveStorageKeys,
  removeStorageKeys,
} from "./storageUtils";

/**
 * Column visibility hook with URL + optional localStorage persistence.
 * - Persists selected columns in query param (default: `cols`).
 * - Omits param when all columns are selected (keeps URL short) unless disabled.
 * - Optional persistence in localStorage (so user sees same selection next visit).
 */
interface UseUrlColumnsOptions {
  allKeys: string[]; // full list of valid column keys
  param?: string; // query param name (default: cols)
  delimiter?: string; // delimiter for joining values (default: ,)
  removeWhenAllSelected?: boolean; // if true remove param when all columns are active
  storage?: { key: string }; // optional localStorage key for persistence
}

export const useUrlColumns = ({
  allKeys,
  param = "cols",
  delimiter = ",",
  removeWhenAllSelected = true,
  storage,
}: UseUrlColumnsOptions) => {
  // Load any stored column selection (if persistence is enabled)
  const storedInitial = storage ? loadStorageKeys(storage.key, allKeys) : [];
  const [persistSelection, setPersistSelection] = useState<boolean>(
    storedInitial.length > 0,
  );

  // URL-backed state for visible column keys
  const { value: visibleKeys, setValue: setVisibleKeysState } = useUrlState<
    string[]
  >({
    param,
    initial: storedInitial.length ? storedInitial : allKeys,
    decode: (raw) => parseArrayParam(raw, allKeys, delimiter),
    encode: (arr) => {
      if (removeWhenAllSelected && arr.length === allKeys.length) return null;
      return joinArrayParam(arr, delimiter);
    },
    // Arrays are reference-unequal even if contents are identical; supply shallow equality
    isEqual: (a, b) => a.length === b.length && a.every((k, i) => k === b[i]),
  });

  // Update visible keys + optionally persist
  const setVisibleKeys = useCallback(
    (next: string[]) => {
      setVisibleKeysState(next);
      if (!storage) return;
      if (persistSelection) saveStorageKeys(storage.key, next);
      else removeStorageKeys(storage.key);
    },
    [setVisibleKeysState, storage, persistSelection],
  );

  // Toggle persistence flag & sync storage immediately
  const setPersist = useCallback(
    (val: boolean) => {
      setPersistSelection(val);
      if (!storage) return;
      if (val) saveStorageKeys(storage.key, visibleKeys);
      else removeStorageKeys(storage.key);
    },
    [storage, visibleKeys],
  );

  return { visibleKeys, setVisibleKeys, persistSelection, setPersist } as const;
};
